import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { FaCog, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { trackEvent, ANALYTICS_EVENTS, setUserProperties } from '../utils/analytics';
import { getErrorMessage } from '../utils/errorMessage';

const MachineSelector = ({ user }) => {
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState('');
  const [customName, setCustomName] = useState('');
  const [volume, setVolume] = useState('');
  // Same L/ml unit toggle as onboarding — eliminates the "1.89 vs 1890"
  // confusion when typing tank capacity here too. Default = ml because
  // most consumer machines are spec'd in ml on the box.
  const [volumeUnit, setVolumeUnit] = useState('ml');
  const [userMachine, setUserMachine] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  // Matches both generic custom and brand-specific "Anden <brand>-model".
  const isCustomChoice = selectedMachine === '__custom__' || selectedMachine.startsWith('__custom_brand_');

  // Group catalog machines by brand
  const machinesByBrand = useMemo(() => {
    const grouped = {};
    for (const m of machines) {
      const brand = (m.brand || 'Andre').trim();
      if (!grouped[brand]) grouped[brand] = [];
      grouped[brand].push(m);
    }
    return Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b, 'da'))
      .map((brand) => [brand, grouped[brand]]);
  }, [machines]);

  useEffect(() => {
    fetchMachines();
    fetchUserMachine();
  }, []);

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API}/machine-catalog/list`);
      setMachines(response.data.machines || []);
    } catch (error) {
      console.error('Error fetching machines:', error);
      toast.error('Kunne ikke hente maskiner');
    }
  };

  const fetchUserMachine = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/user/machine`);
      const m = response.data.machine;
      if (m) {
        setUserMachine(m);
        if (m.is_custom) {
          setSelectedMachine('__custom__');
          setCustomName(m.name || '');
        } else {
          setSelectedMachine(m.name || '');
        }
        const v = m.user_volume_l || m.capacity_l || (m.tank_sizes_l && m.tank_sizes_l[0]);
        if (v) setVolume(String(v));
      }
    } catch (error) {
      console.error('Error fetching user machine:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prefill volume from catalog row when the user picks a catalog machine.
  // Inlined into the change handler (not useEffect) to avoid set-state-in-effect.
  const handleSelectChange = (value) => {
    setSelectedMachine(value);
    if (value === '__custom__') {
      if (!userMachine?.is_custom) setVolume('');
      setVolumeUnit('ml');
      return;
    }
    if (value.startsWith('__custom_brand_')) {
      // Brand-specific custom — pre-fill brand prefix, clear volume, default unit ml.
      const brand = value.replace('__custom_brand_', '');
      setCustomName(`${brand} `);
      setVolume('');
      setVolumeUnit('ml');
      return;
    }
    if (!value) {
      setCustomName('');
      return;
    }
    const m = machines.find((x) => x.name === value);
    if (!m || userMachine?.name === m.name) return;
    const cap = m.capacity_l || (m.tank_sizes_l && m.tank_sizes_l[0]);
    if (cap) {
      // Catalog values are in litres — switch the toggle so the displayed
      // number matches the spec sheet exactly.
      setVolumeUnit('L');
      setVolume(String(cap));
    }
  };

  const handleSave = async () => {
    const machineName = isCustomChoice ? customName.trim() : selectedMachine;
    if (!machineName) {
      toast.error(isCustomChoice ? 'Skriv et navn på din maskine' : 'Vælg en maskine');
      return;
    }
    // Convert typed value to liters based on selected unit. Stored canonical
    // unit is always liters; the toggle is purely a UX clarifier.
    const raw = parseFloat((volume || '').toString().replace(',', '.'));
    const v = Number.isFinite(raw) && raw > 0
      ? (volumeUnit === 'ml' ? raw / 1000 : raw)
      : NaN;
    setSaving(true);
    try {
      const response = await axios.put(`${API}/user/machine`, {
        machine_name: machineName,
        machine_volume_l: Number.isFinite(v) && v > 0 ? v : null,
        is_custom: isCustomChoice,
      });

      setUserMachine({
        name: response.data.machine_name,
        brand: response.data.machine_brand,
        category: response.data.machine_category,
        is_custom: response.data.is_custom,
        capacity_l: response.data.machine_volume_l,
        user_volume_l: response.data.machine_volume_l,
        tank_sizes_l: response.data.tank_sizes_l,
        recommended_brix: response.data.recommended_brix,
      });

      toast.success('Maskine gemt!');
      trackEvent(ANALYTICS_EVENTS.MACHINE_SELECTED, {
        machine_name: response.data.machine_name,
        machine_capacity_l: response.data.machine_volume_l,
        is_custom_machine: !!response.data.is_custom,
        machine_brand: response.data.machine_brand || null,
        source: 'settings',
      });
      // Update GA4 user property so subsequent events are filterable by
      // machine in Path/Funnel Explorations.
      setUserProperties({ machine_name: response.data.machine_name });
    } catch (error) {
      console.error('Error saving machine:', error);
      toast.error(getErrorMessage(error, 'Kunne ikke gemme maskine'));
    } finally {
      setSaving(false);
    }
  };

  const formatCategory = (category) => {
    const labels = {
      hobby: 'Hobby', semi_pro: 'Semi-Pro', pro: 'Pro',
      small: 'Small', medium: 'Medium', large: 'Large',
      high_power: 'High Power', budget: 'Budget', event: 'Event',
      custom: 'Brugerdefineret',
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 text-gray-600">
          <div className="animate-spin h-5 w-5 border-2 border-cyan-500 border-t-transparent rounded-full"></div>
          <span>Henter maskindata...</span>
        </div>
      </div>
    );
  }

  const savedVolume = userMachine?.user_volume_l || userMachine?.capacity_l;
  const currentMachineName = isCustomChoice ? customName.trim() : selectedMachine;
  const currentVolumeNum = parseFloat(volume);
  const isDirty =
    currentMachineName &&
    (currentMachineName !== userMachine?.name ||
      isCustomChoice !== !!userMachine?.is_custom ||
      (Number.isFinite(currentVolumeNum) && currentVolumeNum > 0 && currentVolumeNum !== savedVolume));

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <FaCog className="text-orange-500" />
          Din Slushmaskine
        </h2>
        <button
          type="button"
          onClick={() => setShowInfo(!showInfo)}
          className="text-gray-400 hover:text-gray-600"
        >
          <FaInfoCircle />
        </button>
      </div>

      {showInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p>
            <strong>💡 Tip:</strong> Vælg din maskine fra listen ELLER skriv selv hvis du ikke kan finde din model.
            Volumen bruges til at skalere opskrifter til din maskinestørrelse.
          </p>
        </div>
      )}

      {/* Current Machine Display */}
      {userMachine && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-r-lg p-4">
          <div className="flex items-start gap-3">
            <FaCheckCircle className="text-green-600 text-xl mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-gray-800">{userMachine.name}</div>
              <div className="text-sm text-gray-600 space-y-1 mt-2">
                <div>
                  <span className="font-medium">Brand:</span> {userMachine.brand || '—'}
                </div>
                {savedVolume && (
                  <div>
                    <span className="font-medium">Volumen:</span> {savedVolume}L
                  </div>
                )}
                <div>
                  <span className="font-medium">Kategori:</span> {formatCategory(userMachine.category)}
                </div>
                {userMachine.recommended_brix?.min && (
                  <div>
                    <span className="font-medium">Anbefalet Brix:</span> {userMachine.recommended_brix.min}–{userMachine.recommended_brix.max}°Bx
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Machine Selector */}
      <div className="space-y-2">
        <Label htmlFor="machine-select">
          {userMachine ? 'Skift maskine' : 'Vælg din maskine'}
        </Label>
        <select
          id="machine-select"
          data-testid="profile-machine-select"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          value={selectedMachine}
          onChange={(e) => handleSelectChange(e.target.value)}
        >
          <option value="">-- Vælg en maskine --</option>
          <option value="__custom__">📝 Anden maskine (skriv selv)</option>
          {machinesByBrand.map(([brand, ms]) => (
            <optgroup key={brand} label={brand}>
              {ms.map((m) => (
                <option key={m.name} value={m.name}>
                  {m.name}
                </option>
              ))}
              {/* Per-brand "Anden <brand>-model" escape hatch. */}
              <option value={`__custom_brand_${brand}`}>
                📝 Anden {brand}-model
              </option>
            </optgroup>
          ))}
        </select>
      </div>

      {/* Custom name input */}
      {isCustomChoice && (
        <div className="space-y-2">
          <Label htmlFor="machine-custom-name">Maskinens navn / mærke</Label>
          <input
            id="machine-custom-name"
            data-testid="profile-machine-custom-name"
            type="text"
            placeholder="Fx Ninja Slushi, Wilfa Frost..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
          />
        </div>
      )}

      {/* Volume input — L/ml toggle removes the unit ambiguity that caused
          the "1.89 interpreted as 189L" misread. The conversion preview
          below the input confirms the typed value before save. */}
      <div className="space-y-2">
        <Label htmlFor="machine-volume">Volumen pr. tank</Label>
        <div className="flex gap-2">
          <input
            id="machine-volume"
            data-testid="profile-machine-volume"
            type="number"
            min="0"
            step={volumeUnit === 'ml' ? '10' : '0.1'}
            inputMode="decimal"
            placeholder={volumeUnit === 'ml' ? '1500' : '1.5'}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
          />
          <select
            value={volumeUnit}
            onChange={(e) => setVolumeUnit(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 font-semibold bg-white focus:ring-2 focus:ring-cyan-500"
            data-testid="profile-machine-volume-unit"
            aria-label="Enhed for volumen"
          >
            <option value="L">Liter (L)</option>
            <option value="ml">Milliliter (ml)</option>
          </select>
        </div>
        {(() => {
          const n = parseFloat((volume || '').toString().replace(',', '.'));
          if (!Number.isFinite(n) || n <= 0) return null;
          const liters = volumeUnit === 'ml' ? n / 1000 : n;
          const ml = Math.round(liters * 1000);
          return (
            <p className="text-xs text-blue-700 font-medium" data-testid="profile-volume-preview">
              = {liters.toLocaleString('da-DK', { maximumFractionDigits: 3 })} L ({ml.toLocaleString('da-DK')} ml)
            </p>
          );
        })()}
      </div>

      {/* Save Button */}
      {isDirty && (
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="profile-machine-save"
          className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
        >
          {saving ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
              Gemmer...
            </>
          ) : (
            <>
              <FaCog className="mr-2" />
              Gem Maskine
            </>
          )}
        </Button>
      )}

      {/* Machine count info */}
      <div className="text-xs text-gray-500 text-center">
        {machines.length} maskiner tilgængelige i katalog
      </div>
    </div>
  );
};

export default MachineSelector;
